import store from '@/store/store';
import {
  ioUserLogin,
  ioUserHeartbeat,
  isUserUpdate,
  ioGetAuthCode,
  updatePassword,
  ioUpdateUser,
  ioCreateUser,
  ioGetFileCode,
  getSharedFile,
  sentOtpToVerify,
  requestNewOtp,
  ioCopyRolesByUser,
  ioRemoveUser,
  ioGetProcessInfo
} from '@/febos/servicios/api/usuarios.api';
import { ioUserPermissions } from '@/febos/servicios/api/permisos.api';
import { ioCopyProfilesByUser } from '@/febos/servicios/api/v2.companies.api';

export default {
  /**
   * Login con soporte de 2FA de dos pasos.
   *
   * - codigo == 10: login OK => commitea sesion y devuelve los datos del usuario.
   * - codigo == 11: REQUIERE_2FA => no commitea; devuelve el estado 2FA para que
   *   la vista muestre el ingreso del OTP (metodo, desafioId, correoOfuscado).
   * - cualquier otro codigo: error real => lanza Error con el mensaje de la API.
   *
   * El paso B (validacion del OTP) reusa esta misma action enviando otp + desafioId.
   */
  async signIn({ commit }, payload) {
    const response = await ioUserLogin(payload.correo, payload.clave, {
      otp: payload.otp,
      desafioId: payload.desafioId
    });
    const { data } = response;

    if (data.codigo === 11) {
      return {
        requiere2fa: true,
        metodo: data.metodo,
        desafioId: data.desafioId,
        correoOfuscado: data.correoOfuscado
      };
    }

    if (data.codigo !== 10) {
      const error = new Error(data.mensaje);
      error.context = data;
      throw error;
    }

    commit('SET_USUARIO', data);
    commit('SET_TOKEN', data);
    return data;
  },
  async signInSSO({ commit }, idProceso) {
    const response = await ioGetProcessInfo(idProceso);
    commit('SET_USUARIO', response.data);
    commit('SET_TOKEN', response.data);
    return response.data;
  },
  async signInSSOCU({ commit }, idProceso) {
    const response = await ioGetProcessInfo(idProceso);
    commit('SET_USUARIO', response.data);
    commit('SET_USUARIO_FROM_CU', true);
    commit('SET_TOKEN', response.data);
    return response.data;
  },
  async heartbeat({ getters: { currentUserId }, dispatch }) {
    await ioUserHeartbeat(currentUserId);
    dispatch('validateSession');
  },
  signOut({ commit }) {
    commit('LIMPIAR_SESION');
  },
  async loadPermissions({ commit, getters: { currentUser: { iut } } }) {
    const response = await ioUserPermissions(iut);
    commit('SET_PERMISOS', response.data);
    return response.data;
  },
  validateSession({ commit }) {
    const segundo = 1000;
    const minutos = 60 * segundo;
    commit('SET_ULTIMO_REQUEST', { ultimoRequest: new Date().getTime() });
    commit('SET_VENCIMIENTO_SESION', { vencimiento: new Date().getTime() + ((60 * minutos) * 6) });
  },
  async updateProfile({ commit }, payload) {
    const response = await isUserUpdate(
      payload.id,
      payload.iut,
      payload.nombre,
      payload.alias,
      payload.correo,
      payload.avatar,
      payload.cargo,
      payload.departamento
    );
    commit('SET_USUARIO', { usuario: payload });
    return response.data;
  },
  async setLocalWorkerData({ commit }, payload) {
    commit('SET_USUARIO_WORKER_DATA', { usuario: payload });
  },
  async getVerificationCode({ commit }, payload) {
    commit('SET_LOADING', true);
    const response = await ioGetAuthCode(payload);
    commit('SET_VERIFICATION_CODE', response.data.febosId);
    commit('SET_LOADING', false);
  },
  async updateUserComppany({ commit }, user) {
    try {
      commit('SET_LOADING', true);
      await ioUpdateUser({
        simular: 'no',
        usuarioId: user.id
      }, user);
      store.commit('Empresas/UPDATE_USER', user);
      store.commit('Modals/CLOSE_MODAL');
    } finally {
      commit('SET_LOADING', false);
    }
  },
  async copyUserRoles({ commit }, { userId, selectedUsers }) {
    try {
      commit('SET_LOADING', true);
      const response = await ioCopyRolesByUser({
        params: {
          usuarioId: userId
        },
        body: {
          listaUsuariosRecibir: selectedUsers,
          usuarioId: userId
        }
      });
      store.commit('Modals/CLOSE_MODAL');
      return response;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  async copyUserProfiles({ commit }, {
    userId, companyId, targetUsersIds, reemplazar
  }) {
    try {
      commit('SET_LOADING', true);
      const response = await ioCopyProfilesByUser(
        {
          companyId,
          userId,
          targetUsersIds,
          reemplazar
        }
      );
      return response;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  async ioGetFileCodeVerification({ commit }, identification) {
    try {
      commit('SET_SHARED_FILE', {});
      commit('SET_LOADING', true);
      const response = await ioGetFileCode(identification);
      if (response.data.id) {
        commit('SET_VERIFICATION_CODE', response.data.id);
      } else if (response.data) {
        commit('SET_SHARED_FILE', response.data);
      }
      commit('SET_SUCCESS_MESSAGE', true);
      return response.data;
    } catch (e) {
      commit('SET_ERROR_MESSAGE', e.context);
    } finally {
      commit('SET_LOADING', false);
    }
    return null;
  },
  async verifySharedFile({ commit }, payload) {
    try {
      commit('SET_SHARED_FILE', {});
      commit('SET_LOADING', true);
      const response = await getSharedFile(payload);
      commit('SET_SHARED_FILE', response.data);
      commit('SET_LOADING', false);
    } catch (e) {
      commit('SET_ERROR_MESSAGE', e.context);
    } finally {
      commit('SET_LOADING', false);
    }
  },
  async createUserCompany({ commit }, { user, forGroup }) {
    try {
      commit('SET_LOADING', true);
      const response = await ioCreateUser({
        simular: 'no'
      }, user);
      if (!forGroup) store.commit('Empresas/ADD_USER', response.data.usuario);
      store.commit('Modals/CLOSE_MODAL');
      // commit('SET_SUCCESS_MESSAGE', true);
      return response.data.usuario;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  cleanSuccess({ commit }) {
    commit('CLEAN_SUCCESS_ACTION');
  },
  async removeUserCompany({ commit, rootState }, user) {
    try {
      commit('SET_LOADING', true);
      const response = await ioRemoveUser(user);
      store.commit('Empresas/REMOVE_USER', user.usuarioId);
      store.commit(
        'List/REMOVE_ITEM_FROM_INDEXED_LIST',
        {
          target: 'users',
          indexedBy: rootState.Empresas.empresa.id,
          elementId: user.usuarioId
        }
      );
      store.commit(
        'List/REMOVE_ITEM_FROM_INDEXED_LIST',
        {
          target: 'users',
          indexedBy: `${rootState.Empresas.empresa.id}-7441a75e-80b9-486a-8595-34c487a56dd9`,
          elementId: user.usuarioId
        }
      );
      store.commit('Modals/CLOSE_MODAL');
      commit('SET_SUCCESS_ACTION', { show: true, message: response.data.mensaje });
    } finally {
      commit('SET_LOADING', false);
    }
  },
  async changePassword({ commit }, passwords) {
    try {
      commit('SET_LOADING', true);
      await updatePassword(passwords);
      commit('SET_SUCCESS_MESSAGE', true);
    } finally {
      commit('SET_LOADING', false);
    }
  },
  async sendNewOTP({ commit }, user) {
    try {
      commit('SET_LOADING', true);
      return requestNewOtp(user);
    } finally {
      commit('SET_LOADING', false);
    }
  },
  async verifyOTP({ commit }, { otp, user }) {
    try {
      commit('SET_LOADING', true);
      return sentOtpToVerify(otp, user);
    } finally {
      commit('SET_LOADING', false);
    }
  }
};
